import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    signal,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Dayjs } from 'dayjs';
import { FormBuilder } from '@angular/forms';
import {
    BasketItem,
    BasketItemRequest,
    CompanySetting,
    SupplierBasket,
    TotalBasket,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { EnvService } from '@lib/env';
import {
    BudgetAssignmentType,
    IBudgetCostObject,
    IBudgetExceedance,
    IPurchasingGroupAssignmentIssue,
    IPurchasingGroupResolution,
} from '@lib/shared/basket-service';

@Component({
    selector: 'suppliers-basket-overview',
    templateUrl: './suppliers-basket-overview.component.html',
    styleUrls: ['./suppliers-basket-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SuppliersBasketOverviewComponent implements OnChanges {
    @ViewChild('supplierBasketsContainer', { read: ElementRef }) supplierBasketsContainer!: ElementRef;

    @Input() supplierBaskets: SupplierBasket[] = [];
    @Input() currencyTotals: TotalBasket[] = [];
    @Input() showStepperButtons = false;
    @Input() isEditableStep!: boolean;
    @Input() companySetting!: CompanySetting;
    @Input() basketToggleStatus = false;
    @Input() budgetOverruns: IBudgetExceedance[] | null = [];
    @Input() purchasingGroupAssignmentIssues: readonly IPurchasingGroupAssignmentIssue[] = [];
    @Input() resolvedPurchasingGroups: readonly IPurchasingGroupResolution[] = [];
    @Input() purchasingGroupVisible = true;
    @Input() purchasingGroupValidationLoading = false;
    @Input() companyCodeId: string | null = null;

    @Output() nextClicked = new EventEmitter();
    @Output() validityChanged = new EventEmitter<boolean>();

    showProductNumberValidationMessage = false;

    date!: Dayjs;

    env = EnvService;

    today!: Dayjs;

    formBuilder = inject(FormBuilder);
    budgetAssignmentType = BudgetAssignmentType;

    accounting = signal(new Map<number, boolean>());
    classification = signal(new Map<number, boolean>());
    materialGroup = signal(new Map<number, boolean>());
    siteValid = signal(new Map<number, boolean>());
    deliveryDateValid = signal(new Map<number, boolean>());
    paymentTermsValid = signal(new Map<string, boolean>());
    frameContractsValid = signal(new Map<string, boolean>());
    invalidPurchasingGroupBasketItemIds = signal(new Set<string>());

    onClickNext(): void {
        this.emitCurrentValidity();
        this.nextClicked.emit();
    }

    onShowProductNumberValidationMessage(isShowMessage: boolean) {
        this.showProductNumberValidationMessage = isShowMessage;
        this.emitCurrentValidity();
    }

    onDeleteBasketItem(basketItemRequest: BasketItemRequest) {
        const updatedClassification = new Map(this.classification());
        const updatedAccounting = new Map(this.accounting());
        const updatedDeliveryDateValid = new Map(this.deliveryDateValid());
        const updatedPaymentTerms = new Map(this.paymentTermsValid());
        const updatedFrameContracts = new Map(this.frameContractsValid());

        const updatedMaterialGroup = new Map(this.materialGroup());
        const updatedSiteValid = new Map(this.siteValid());

        updatedClassification.delete(basketItemRequest.basketItem.id);
        updatedAccounting.delete(basketItemRequest.basketItem.id);
        updatedMaterialGroup.delete(basketItemRequest.basketItem.id);
        updatedSiteValid.delete(basketItemRequest.basketItem.id);
        updatedDeliveryDateValid.delete(basketItemRequest.basketItem.id);
        updatedPaymentTerms.delete(basketItemRequest.supplierBasketId);
        updatedFrameContracts.delete(basketItemRequest.supplierBasketId);

        this.classification.set(updatedClassification);
        this.accounting.set(updatedAccounting);
        this.materialGroup.set(updatedMaterialGroup);
        this.siteValid.set(updatedSiteValid);
        this.deliveryDateValid.set(updatedDeliveryDateValid);
        this.paymentTermsValid.set(updatedPaymentTerms);
        this.frameContractsValid.set(updatedFrameContracts);
        this.emitCurrentValidity();
    }

    isAccountingValid(): boolean {
        const accountingArray = Array.from(this.accounting().values());
        return accountingArray.every((el) => !!el);
    }

    isClassificationValid(): boolean {
        const classificationArray = Array.from(this.classification().values());
        return classificationArray.every((el) => !!el);
    }

    isMaterialGroupValid(): boolean {
        const materialGroupArray = Array.from(this.materialGroup().values());
        return materialGroupArray.every((el) => !!el);
    }

    isDeliveryDateValid(): boolean {
        const deliveryDateArray = Array.from(this.deliveryDateValid().values());
        return deliveryDateArray.every((el) => !!el);
    }

    // EPROC-10907: every visible site selector reports validity; required + empty blocks checkout.
    isSiteValid(): boolean {
        return Array.from(this.siteValid().values()).every((el) => !!el);
    }

    isPurchasingGroupAssignmentValid(): boolean {
        return !this.purchasingGroupValidationLoading && this.invalidPurchasingGroupBasketItemIds().size === 0;
    }

    isPaymentTermsValid = computed(() => {
        const paymentTermsArray = Array.from(this.paymentTermsValid().values());
        return paymentTermsArray.every((el) => !!el);
    });

    isFrameContractsValid = computed(() => {
        const frameContractsArray = Array.from(this.frameContractsValid().values());
        return frameContractsArray.every((el) => !!el);
    });

    checkSupplierItemAccounting(item: BasketItem): boolean {
        const value = this.accounting().get(item.id);
        return value !== undefined && value === false;
    }

    checkSupplierItemClassification(item: BasketItem): boolean {
        const value = this.classification().get(item.id);
        return value !== undefined && value === false;
    }

    checkSupplierItemMaterialGroup(item: BasketItem): boolean {
        const value = this.materialGroup().get(item.id);
        return value !== undefined && value === false;
    }

    checkSupplierItemPurchasingGroup(item: BasketItem): boolean {
        return this.invalidPurchasingGroupBasketItemIds().has(String(item.id));
    }

    scrollToItemById(itemId: string): void {
        const containerElement: HTMLElement | null = this.supplierBasketsContainer.nativeElement;

        if (containerElement) {
            const element = containerElement.querySelector(`section[id="${itemId}"]`);

            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            }
        }
    }

    onAccountingValidated(event: { valid: boolean; basketItemId: number }) {
        const updatedAccounting = new Map(this.accounting());
        updatedAccounting.set(event.basketItemId, event.valid);
        this.accounting.set(updatedAccounting);
        this.emitCurrentValidity();
    }

    onClassificationValidated(event: { valid: boolean; basketItemId: number }) {
        const updatedClassification = new Map(this.classification());
        updatedClassification.set(event.basketItemId, event.valid);
        this.classification.set(updatedClassification);
        this.emitCurrentValidity();
    }

    onMaterialGroupValidated(event: { valid: boolean; basketItemId: number }) {
        const updatedMaterialGroup = new Map(this.materialGroup());
        updatedMaterialGroup.set(event.basketItemId, event.valid);
        this.materialGroup.set(updatedMaterialGroup);
        this.emitCurrentValidity();
    }

    onDeliveryDateValidated(event: { valid: boolean; basketItemId: number }) {
        const updatedDeliveryDateValid = new Map(this.deliveryDateValid());
        updatedDeliveryDateValid.set(event.basketItemId, event.valid);
        this.deliveryDateValid.set(updatedDeliveryDateValid);
        this.emitCurrentValidity();
    }

    onSiteValidated(event: { valid: boolean; basketItemId: number }) {
        const updatedSiteValid = new Map(this.siteValid());
        updatedSiteValid.set(event.basketItemId, event.valid);
        this.siteValid.set(updatedSiteValid);
        this.emitCurrentValidity();
    }

    onPaymentTermsValidated(event: { valid: boolean; basketSupplierId: string }) {
        const updatedPaymentTerms = new Map(this.paymentTermsValid());
        updatedPaymentTerms.set(event.basketSupplierId, event.valid);
        this.paymentTermsValid.set(updatedPaymentTerms);
        this.emitCurrentValidity();
    }

    onFrameContractValidated(event: { valid: boolean; basketSupplierId: string }) {
        const updatedFrameContracts = new Map(this.frameContractsValid());
        updatedFrameContracts.set(event.basketSupplierId, event.valid);
        this.frameContractsValid.set(updatedFrameContracts);
        this.emitCurrentValidity();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes['supplierBaskets']) {
            this.pruneValidationState();
        }

        if (changes['supplierBaskets'] || changes['purchasingGroupAssignmentIssues']) {
            this.syncInvalidPurchasingGroupBasketItemIds();
        }

        if (changes['purchasingGroupValidationLoading']) {
            this.emitCurrentValidity();
        }

        if (changes['supplierBaskets'] || changes['purchasingGroupAssignmentIssues']) {
            this.emitCurrentValidity();
        }
    }

    getCostObject(type: BudgetAssignmentType, costObjects?: readonly IBudgetCostObject[]) {
        return costObjects?.find((costObject) => costObject.type === type);
    }

    hasPurchasingGroupAssignmentIssues(): boolean {
        return this.invalidPurchasingGroupBasketItemIds().size > 0;
    }

    private emitCurrentValidity(): void {
        this.validityChanged.emit(
            this.isAccountingValid() &&
                this.isClassificationValid() &&
                this.isMaterialGroupValid() &&
                !this.showProductNumberValidationMessage &&
                this.isSiteValid() &&
                this.isDeliveryDateValid() &&
                this.isPaymentTermsValid() &&
                this.isFrameContractsValid() &&
                this.isPurchasingGroupAssignmentValid(),
        );
    }

    private syncInvalidPurchasingGroupBasketItemIds(): void {
        const visibleBasketItemIds = new Set(
            this.supplierBaskets.flatMap((supplierBasket) => supplierBasket.basketItems.map((item) => String(item.id))),
        );

        this.invalidPurchasingGroupBasketItemIds.set(
            new Set(
                (this.purchasingGroupAssignmentIssues ?? [])
                    .map((issue) => issue.basketItemId)
                    .filter((basketItemId) => visibleBasketItemIds.has(basketItemId)),
            ),
        );
    }

    private pruneValidationState(): void {
        const visibleBasketItemIds = new Set(
            this.supplierBaskets.flatMap((supplierBasket) => supplierBasket.basketItems.map((item) => item.id)),
        );
        const visibleSupplierBasketIds = new Set(this.supplierBaskets.map((supplierBasket) => supplierBasket.id));

        this.accounting.set(this.filterMapByKeys(this.accounting(), visibleBasketItemIds));
        this.classification.set(this.filterMapByKeys(this.classification(), visibleBasketItemIds));
        this.materialGroup.set(this.filterMapByKeys(this.materialGroup(), visibleBasketItemIds));
        this.siteValid.set(this.filterMapByKeys(this.siteValid(), visibleBasketItemIds));
        this.deliveryDateValid.set(this.filterMapByKeys(this.deliveryDateValid(), visibleBasketItemIds));
        this.paymentTermsValid.set(this.filterMapByKeys(this.paymentTermsValid(), visibleSupplierBasketIds));
        this.frameContractsValid.set(this.filterMapByKeys(this.frameContractsValid(), visibleSupplierBasketIds));
    }

    private filterMapByKeys<TKey, TValue>(source: ReadonlyMap<TKey, TValue>, visibleKeys: ReadonlySet<TKey>): Map<TKey, TValue> {
        return new Map(Array.from(source.entries()).filter(([key]) => visibleKeys.has(key)));
    }
}
